import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import { useAuth } from "./AuthContext";

const REMEMBER_KEY = "rememberedEmail";

const fetchWithTimeout = (resource, options = {}, timeout = 15000) => {
  return Promise.race([
    fetch(resource, options),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Request timed out")), timeout),
    ),
  ]);
};

/* ---------------- small icons (inline, no extra packages) ---------------- */

const MailIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2.5" />
    <path d="m4 7 8 6 8-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <rect x="4.5" y="10.5" width="15" height="9.5" rx="2.5" />
    <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" strokeLinecap="round" />
  </svg>
);

const EyeIcon = ({ off }) => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <path d="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12Z" strokeLinejoin="round" />
    <circle cx="12" cy="12" r="3" />
    {off && <path d="m4 4 16 16" strokeLinecap="round" />}
  </svg>
);

/* ---------------- left brand panel ---------------- */

const BrandPanel = () => (
  <aside className="relative hidden w-[46%] max-w-[640px] flex-col justify-between overflow-hidden bg-[#143F7A] p-12 text-white lg:flex">
    {/* route line: the one memorable element */}
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      viewBox="0 0 600 800"
      preserveAspectRatio="xMidYMid slice"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M-20 640 C 120 560, 140 420, 260 400 S 420 300, 380 190 S 520 80, 640 60"
        stroke="#393939"
        strokeWidth="14"
        strokeLinecap="round"
      />
      <path
        d="M-20 640 C 120 560, 140 420, 260 400 S 420 300, 380 190 S 520 80, 640 60"
        stroke="#f5b942"
        strokeWidth="2.5"
        strokeDasharray="2 14"
        strokeLinecap="round"
      />
      {[
        [260, 400],
        [380, 190],
      ].map(([x, y]) => (
        <g key={`${x}-${y}`}>
          <circle cx={x} cy={y} r="16" fill="#0d2a3a" stroke="#f5b942" strokeWidth="3" />
          <circle cx={x} cy={y} r="5" fill="#f5b942" />
        </g>
      ))}
    </svg>

    <div className="relative flex items-center gap-3">
     
<img
  src="/logo.png"
  alt="WheelsERP Prime"
  className="h-14 w-14 object-contain"
/>

      <div className="leading-tight">
        <p className="text-xs text-white/70">Lotus</p>
       <p className="text-2xl font-semibold tracking-tight">
             WheelsERP <span className="italic text-sky-300">Prime</span>
        </p>
      </div>
    </div>

    <div className="relative max-w-md">
      <h1 className="text-4xl font-semibold leading-[1.15] tracking-tight">
        Every booking, trip and bill in one place.
      </h1>
      <p className="mt-5 text-base leading-relaxed text-white/75">
        Manage bookings, trip sheets, fuel indents and invoicing for your
        fleet, with accounts that stay in step with the road.
      </p>
    </div>

    <p className="relative text-sm text-white/55">
      &copy; {new Date().getFullYear()} Lotus Incorporation
    </p>
  </aside>
);

/* ---------------- page ---------------- */

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const { login } = useAuth();
  const { setCompany } = useAppContext();
  const navigate = useNavigate();

  // restore the remembered email (the password is never stored)
  useEffect(() => {
    try {
      const saved = localStorage.getItem(REMEMBER_KEY);
      if (saved) {
        setEmail(saved);
        setRemember(true);
      }
    } catch {
      /* storage unavailable */
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setError(null);
    setSubmitting(true);

    try {
      const response = await fetchWithTimeout(
        "/api/auth/login",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email: email.trim(), password }),
        },
        15000,
      );

      let data;
      try {
        data = await response.json();
      } catch {
        throw new Error("Invalid server response");
      }

      // Trial ended: go to the activation page (checked first,
      // because the server may answer this with an error status)
      if (data.trialExpired) {
        localStorage.setItem("trialClientId", data.client_id);
        navigate("/subscription-expired");
        return;
      }

      // Wrong password, inactive user, etc.
      if (!response.ok) {
        throw new Error(data.message || "Incorrect email or password.");
      }

      // Remember the email only
      try {
        if (remember) localStorage.setItem(REMEMBER_KEY, email.trim());
        else localStorage.removeItem(REMEMBER_KEY);
      } catch {
        /* storage unavailable */
      }

      // First login with a temporary password
      if (data.forcePasswordChange) {
        if (data.user_id) {
          localStorage.setItem("forcePasswordUserId", String(data.user_id));
        }

        if (data.token) {
          localStorage.setItem("forcePasswordToken", data.token);
        }

        navigate("/change-password", {
          state: {
            userId: data.user_id,
            email: data.email,
          },
        });

        return;
      }

      // Store user + token in context/localStorage
      login(data.user, data.token);

      // Company context handling
      if (data.company) {
        setCompany(data.company);
      } else if (data.user?.client_id) {
        try {
          const companyResponse = await fetch(
            `/api/clients/${data.user.client_id}`,
            { credentials: "include" },
          );
          if (companyResponse.ok) {
            const companyData = await companyResponse.json();
            setCompany(companyData);
          } else {
            setCompany(null);
          }
        } catch {
          setCompany(null);
        }
      } else {
        setCompany({ name: "Zink", logo: "/logo.png" }); // fallback
      }

      const userId = data.user?.user_id || data.user?.id;
      if (userId) {
        localStorage.setItem("userId", userId);
      }

      // Full reload so all contexts and pages mount with the token
      window.location.replace("/");
    } catch (err) {
      console.error("Login error:", err);

      if (err.message === "Request timed out") {
        setError("The request timed out. Check your connection and try again.");
      } else {
        setError(err.message || "Something went wrong. Please try again.");
      }

      setSubmitting(false);
    }
  };

  const inputWrap =
    "relative flex items-center rounded-lg border border-slate-300 bg-white transition focus-within:border-[#0f6f8f] focus-within:ring-4 focus-within:ring-[#0f6f8f]/15";

  const inputBase =
    "w-full bg-transparent py-3 text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none";

  return (
    <div className="flex min-h-screen bg-white">
      <BrandPanel />

      <main className="flex flex-1 items-center justify-center bg-slate-50 px-5 py-10 sm:px-10">
        <div className="w-full max-w-[420px]">
          {/* compact brand for small screens */}
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <img src="/logo.png" alt="" className="h-10 w-10 rounded-lg bg-white p-1 shadow-sm ring-1 ring-slate-200" />
            <div className="leading-tight">
              <p className="text-xs text-slate-500">Lotus</p>
              <p className="text-xl font-semibold text-slate-900">WheelsERP</p>
            </div>
          </div>

          <div className="rounded-2xl bg-white p-8 shadow-[0_10px_40px_-12px_rgba(13,42,58,0.18)] ring-1 ring-slate-200 sm:p-9">
            <h2 className="text-[26px] font-semibold tracking-tight text-slate-900">
              Sign in to your account
            </h2>
            <p className="mt-1.5 text-sm text-slate-500">
              Use the email and password registered for your company.
            </p>

            {error && (
              <div
                role="alert"
                className="mt-6 flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-800"
              >
                <svg viewBox="0 0 24 24" className="mt-0.5 h-5 w-5 shrink-0 text-red-500" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7.5v5.5M12 16.5h.01" strokeLinecap="round" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-7 space-y-5" noValidate={false}>
              <div>
                <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-slate-700">
                  Email address
                </label>
                <div className={inputWrap}>
                  <span className="pl-3.5 pr-2.5 text-slate-400">
                    <MailIcon />
                  </span>
                  <input
                    id="email"
                    type="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    autoFocus
                    className={`${inputBase} pr-3.5`}
                  />
                </div>
              </div>

              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-sm font-medium text-[#0f6f8f] hover:underline focus-visible:rounded focus-visible:outline-2 focus-visible:outline-[#0f6f8f]"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className={inputWrap}>
                  <span className="pl-3.5 pr-2.5 text-slate-400">
                    <LockIcon />
                  </span>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className={`${inputBase} pr-1`}
                  />
                  
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="mr-1.5 rounded-md p-2 text-slate-400 transition hover:text-blue-700 focus-visible:outline-2 focus-visible:outline-[#0D2A3A]"
                  >
                    <EyeIcon off={showPassword} />
                  </button>
                </div>
              </div>

              <label className="flex cursor-pointer items-center gap-2.5 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-[#0f6f8f] focus:ring-[#0f6f8f]"
                />
                Remember my email on this device
              </label>

              <button
                type="submit"
                disabled={submitting}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-800 px-4 py-3 text-[15px] font-semibold text-white shadow-sm transition hover:bg-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {submitting ? (
                  <>
                    <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.3" strokeWidth="3" />
                      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                    Signing in...
                  </>
                ) : (
                  "Sign in"
                )}
              </button>
            </form>
          </div>

          <p className="mt-6 text-center text-sm text-slate-500">
            New to WheelsERP? Contact your administrator or Lotus support to
            get an account.
          </p>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
